import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';

export default function StockPanel({ stock, setStock }) {
  const fileRef = useRef();
  const [draft, setDraft] = useState({ w: '', h: '', qty: '' });

  function updateRow(index, field, val) {
    setStock(stock.map((s, i) => {
      if (i !== index) return s;
      const updated = { ...s, [field]: val };
      return updated;
    }));
  }

  function commitRow(index, field, raw) {
    const num = field === 'qty' ? parseInt(raw) : parseFloat(raw);
    if (!num || num <= 0) {
      // revert to previous
      setStock([...stock]);
      return;
    }
    setStock(stock.map((s, i) => (i === index ? { ...s, [field]: num } : s)));
  }

  function removeRow(index) {
    setStock(stock.filter((_, i) => i !== index));
  }

  function commitDraft() {
    const fw = parseFloat(draft.w);
    const fh = parseFloat(draft.h);
    const iq = parseInt(draft.qty);
    if (!fw || !fh || !iq || fw <= 0 || fh <= 0 || iq <= 0) return;
    const existing = stock.find((s) => s.w === fw && s.h === fh);
    if (existing) {
      setStock(stock.map((s) => (s.w === fw && s.h === fh ? { ...s, qty: s.qty + iq } : s)));
    } else {
      setStock([...stock, { w: fw, h: fh, qty: iq }]);
    }
    setDraft({ w: '', h: '', qty: '' });
  }

  function handleDraftKey(e, nextId) {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (nextId) {
        e.preventDefault();
        document.getElementById(nextId)?.focus();
      } else {
        commitDraft();
      }
    }
  }

  function exportExcel() {
    const rows = stock.map((s) => ({ W: s.w, H: s.h, Qty: s.qty }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, 'stock.xlsx');
  }

  function parseAndMerge(items) {
    let next = [...stock];
    for (const item of items) {
      const idx = next.findIndex((s) => s.w === item.w && s.h === item.h);
      if (idx >= 0) next[idx] = { ...next[idx], qty: next[idx].qty + item.qty };
      else next.push(item);
    }
    setStock(next);
  }

  function importExcel(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const items = rows
        .filter((r) => !isNaN(parseFloat(r[0])))
        .map((r) => ({ w: parseFloat(r[0]), h: parseFloat(r[1]), qty: parseInt(r[2]) }))
        .filter((r) => r.w > 0 && r.h > 0 && r.qty > 0);
      parseAndMerge(items);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const items = text.trim().split('\n')
      .map((line) => line.split('\t').map((v) => v.trim()))
      .filter((r) => !isNaN(parseFloat(r[0])))
      .map((r) => ({ w: parseFloat(r[0]), h: parseFloat(r[1]), qty: parseInt(r[2]) }))
      .filter((r) => r.w > 0 && r.h > 0 && r.qty > 0);
    if (items.length > 0) {
      e.preventDefault();
      parseAndMerge(items);
    }
  }

  return (
    <div className="panel" onPaste={handlePaste}>
      <div className="panel-header-row">
        <div>
          <h2 className="panel-title">Warehouse stock</h2>
          <p className="panel-desc">Glass sheets available for cutting</p>
        </div>
        <div className="panel-actions">
          <button className="btn-xl-action btn-xl-danger" onClick={() => setStock([])} title="Clear all sheets" disabled={stock.length === 0}>Clear all</button>
          <button className="btn-xl-action" onClick={exportExcel} title="Export to Excel">↓ Excel</button>
          <button className="btn-xl-action" onClick={() => fileRef.current.click()} title="Import Excel file">↑ Import</button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={importExcel} />
        </div>
      </div>

      <div className="xl-table-wrap">
        <table className="xl-table">
          <thead>
            <tr>
              <th>W (cm)</th>
              <th>H (cm)</th>
              <th>Qty</th>
              <th>Area (cm²)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {stock.map((s, i) => (
              <tr key={i} className="xl-row">
                <td>
                  <input
                    className="xl-cell-input"
                    type="number"
                    value={s.w}
                    min="0.1"
                    step="0.5"
                    onChange={(e) => updateRow(i, 'w', e.target.value)}
                    onBlur={(e) => commitRow(i, 'w', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="xl-cell-input"
                    type="number"
                    value={s.h}
                    min="0.1"
                    step="0.5"
                    onChange={(e) => updateRow(i, 'h', e.target.value)}
                    onBlur={(e) => commitRow(i, 'h', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="xl-cell-input"
                    type="number"
                    value={s.qty}
                    min="1"
                    onChange={(e) => updateRow(i, 'qty', e.target.value)}
                    onBlur={(e) => commitRow(i, 'qty', e.target.value)}
                  />
                </td>
                <td className="xl-cell-ro">{(s.w * s.h).toFixed(1)}</td>
                <td><button className="btn-remove" onClick={() => removeRow(i)}>×</button></td>
              </tr>
            ))}

            {/* Draft / new row */}
            <tr className="xl-row xl-draft-row">
              <td>
                <input
                  id="stock-draft-w"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.w}
                  min="0.1"
                  step="0.5"
                  placeholder="W"
                  onChange={(e) => setDraft({ ...draft, w: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, 'stock-draft-h')}
                />
              </td>
              <td>
                <input
                  id="stock-draft-h"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.h}
                  min="0.1"
                  step="0.5"
                  placeholder="H"
                  onChange={(e) => setDraft({ ...draft, h: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, 'stock-draft-qty')}
                />
              </td>
              <td>
                <input
                  id="stock-draft-qty"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.qty}
                  min="1"
                  placeholder="Qty"
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, null)}
                  onBlur={commitDraft}
                />
              </td>
              <td className="xl-cell-ro" style={{ color: 'var(--text-muted)' }}>
                {draft.w && draft.h ? (parseFloat(draft.w) * parseFloat(draft.h) || 0).toFixed(1) : '—'}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {stock.length > 0 && (
        <div className="xl-footer">
          <span>{stock.reduce((a, s) => a + s.qty, 0)} sheets</span>
          <span>{stock.reduce((a, s) => a + s.w * s.h * s.qty, 0).toFixed(0)} cm² total</span>
        </div>
      )}
    </div>
  );
}
